<template>
  <div
    :class="['spm-message', `spm-message--${severity}`, { 'spm-message--closable': closable }]"
    role="alert"
  >
    <div class="spm-message__icon">
      <i :class="iconClass" />
    </div>
    <div class="spm-message__content">
      <slot />
    </div>
    <button
      v-if="closable"
      type="button"
      class="spm-message__close"
      aria-label="Close"
      @click="$emit('close')"
    >
      <i class="fa-solid fa-xmark" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';

type MessageSeverity = 'info' | 'success' | 'warning' | 'error';

export default defineComponent({
  name: 'SpmMessage',

  props: {
    severity: {
      type: String as PropType<MessageSeverity>,
      default: 'info',
      validator: (value: string) => ['info', 'success', 'warning', 'error'].includes(value),
    },

    closable: {
      type: Boolean,
      default: false,
    },

    icon: {
      type: String,
      default: null,
    },
  },

  emits: ['close'],

  setup(props) {
    const iconClass = computed(() => {
      if (props.icon) return props.icon;

      const icons: Record<MessageSeverity, string> = {
        info: 'fa-solid fa-circle-info',
        success: 'fa-solid fa-circle-check',
        warning: 'fa-solid fa-triangle-exclamation',
        error: 'fa-solid fa-circle-xmark',
      };

      return icons[props.severity as MessageSeverity];
    });

    return {
      iconClass,
    };
  },
});
</script>

<style scoped>
.spm-message {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid;
  font-size: 0.875rem;
  line-height: 1.5;
}

.spm-message__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.spm-message__icon i {
  font-size: 1rem;
}

.spm-message__content {
  flex: 1;
  min-width: 0;
  color: inherit;
}

.spm-message__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}

.spm-message__close:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.05);
}

.spm-message__close i {
  font-size: 0.875rem;
}

/* Info Severity */
.spm-message--info {
  background: linear-gradient(to right, #eff6ff 0%, #f5f9ff 100%, rgba(255, 255, 255, 0.95) 100%);
  border-color: #93c5fd;
  color: #1e40af;
}

.spm-message--info .spm-message__icon {
  background: #3b82f6;
  color: #ffffff;
}

.spm-message--info .spm-message__close {
  color: #1e40af;
}

/* Success Severity */
.spm-message--success {
  background: linear-gradient(to right, #f0fdf4 0%, #f7fef9 100%, rgba(255, 255, 255, 0.95) 100%);
  border-color: #86efac;
  color: #166534;
}

.spm-message--success .spm-message__icon {
  background: #22c55e;
  color: #ffffff;
}

.spm-message--success .spm-message__close {
  color: #166534;
}

/* Warning Severity */
.spm-message--warning {
  background: linear-gradient(to right, #fffbeb 0%, #fffdf5 100%, rgba(255, 255, 255, 0.95) 100%);
  border-color: #fcd34d;
  color: #92400e;
}

.spm-message--warning .spm-message__icon {
  background: #f59e0b;
  color: #ffffff;
}

.spm-message--warning .spm-message__close {
  color: #92400e;
}

/* Error Severity */
.spm-message--error {
  background: linear-gradient(to right, #fef2f2 0%, #fef8f8 100%, rgba(255, 255, 255, 0.95) 100%);
  border-color: #fca5a5;
  color: #991b1b;
}

.spm-message--error .spm-message__icon {
  background: #ef4444;
  color: #ffffff;
}

.spm-message--error .spm-message__close {
  color: #991b1b;
}
</style>
